// Hook personnalisé pour utiliser le contexte
import { createContext, useContext, useEffect, useState } from "react";
import { PostMetadata } from "@/components/common/posts/post-metadata";
import { useLocation } from "react-router-dom";
import SectionPage from "../section-page";


// Définir le type pour le contexte
interface PostsContextType {
    posts: PostMetadata[];
    postsBasePath: string,
    currentPost: PostMetadata | undefined;
    setCurrentPost: (post: PostMetadata | undefined) => void;
}

// Créer le contexte
const PostsContext = createContext<PostsContextType | undefined>(undefined);

export const usePosts = () => {
    const context = useContext(PostsContext);
    if (context === undefined) {
        throw new Error('useActivitesOffice must be used within a ActivitesOfficeProvider');
    }
    return context;
};


export function PostsProvider({ children, storageKey, postsBasePath, posts }: {
    children: React.ReactNode, storageKey: string,
    postsBasePath: string,
    posts: PostMetadata[]
}) {
    // État pour suivre l'article actuellement sélectionné
    const [currentPost, seetCurrentPostState] = useState<PostMetadata | undefined>(undefined);
    const { pathname } = useLocation();

    // Fonction pour mettre à jour l'article courant et le persister
    const setCurrentPost = (post: PostMetadata | undefined) => {
        seetCurrentPostState(post);

        // Persister dans localStorage
        if (post) {
            localStorage.setItem(storageKey, JSON.stringify(post));
        } else {
            localStorage.removeItem(storageKey);
        }
    };

    // Charger l'article depuis localStorage au chargement initial
    useEffect(() => {
        // Vérifier si nous sommes sur une page de détail d'article
        const isDetailPage = pathname?.includes(postsBasePath + '/') &&
            pathname !== postsBasePath;

        if (isDetailPage) {
            // Extraire le slug de l'URL
            const slug = pathname?.split('/').pop();

            // Essayer de récupérer depuis localStorage d'abord
            const savedArticle = localStorage.getItem(storageKey);

            if (savedArticle) {
                try {
                    const parsedArticle = JSON.parse(savedArticle) as PostMetadata;
                    // Vérifier si l'article sauvegardé correspond à l'URL actuelle
                    if (parsedArticle.slug === slug) {
                        seetCurrentPostState(parsedArticle);
                        return;
                    }
                } catch (e) {
                    console.error('Erreur lors de la récupération de l\'article sauvegardé:', e);
                }
            }

            // Si pas d'article sauvegardé ou ne correspond pas, chercher dans la liste
            if (slug) {
                const matchingArticle = posts.find(post => post.slug === slug);
                if (matchingArticle) {
                    setCurrentPost(matchingArticle);
                }
            }
        } else if (!isDetailPage) {
            // Si nous sommes sur la page de liste, réinitialiser l'article courant
            setCurrentPost(undefined);
        }
    }, [pathname]);

    // Valeur du contexte
    const contextValue: PostsContextType = {
        posts: posts,
        currentPost: currentPost,
        setCurrentPost: setCurrentPost,
        postsBasePath
    };

    return (
        <PostsContext.Provider value={contextValue}>
            {children}
        </PostsContext.Provider>
    );
}


/**
 * Un hook pour charger l'article correspondant au slug de l'URL si nécessaire.
 * @returns {[PostMetadata[] | undefined, PostMetadata | undefined, (post: PostMetadata | undefined) => void, boolean]} Un tuple contenant la liste des articles, l'article courant, une fonction pour mettre à jour l'article courant et un indicateur de chargement.
 */
export function useLoadPostBySlug(slug: string): {
    posts: PostMetadata[];
    currentPost: PostMetadata | undefined;
    setCurrentPost: (post: PostMetadata | undefined) => void;
    isLoading: boolean,
    postsBasePath: string
} {
    const { posts, currentPost, setCurrentPost, postsBasePath } = usePosts();
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Charger l'article si nécessaire
        if (!currentPost) {
            const post = posts.find(post => post.slug === slug);
            if (post) {
                setCurrentPost(post);
            }
            setIsLoading(false);
        } else {
            setIsLoading(false);
        }
    }, [posts, currentPost, setCurrentPost, slug]);

    return { posts, currentPost, setCurrentPost, isLoading, postsBasePath };
}


export function PostContentFallback({ children, fallbackMessage }: { children: React.ReactNode, fallbackMessage?: string }) {
    const { currentPost } = usePosts();

    if (!currentPost) {
        return (
            <SectionPage sectionType={"2xl"}>
                <div className="flex justify-center items-center h-[400px]">
                    <p className="text-xl">{fallbackMessage || "Chargement de l'article..."}</p>
                </div>
            </SectionPage>
        );
    }

    return <>
        {children}
    </>;
}